import React, { useState, useMemo, useEffect } from "react";
import { Breadcrumb, Card, Col, Row, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faEye } from "@fortawesome/free-solid-svg-icons";
import AdvanceTable from "components/common/advance-table/advanceTable";
import AdvanceTableSearchBox from "components/common/advance-table/advanceTableSearchBox";
import AdvanceTableFooter from "components/common/advance-table/advanceTableFooter";
import useAdvanceTable from "hooks/useAdvanceTable";
import AdvanceTableProvider from "providers/advanceTableProvider";
import { useTranslation } from "react-i18next";
import { formatDate, getInitialPageSize } from "assets/js/common";
import { getDomainConfig } from "config/domainConfig";
import { Link } from "react-router-dom";
import Background from "components/common/background";
import corner4 from "assets/img/illustrations/corner-4.png";
import axios from "axios";
import { useAppContext } from "../../providers/appProvider";
import { USER_ROLES } from "config/roles.config";

const formatHoursVerbose = (decimalHours) => {
  if (!decimalHours || isNaN(decimalHours)) return "00:00";
  const hours = Math.floor(decimalHours);
  const minutes = Math.round((decimalHours - hours) * 60);
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
};

function Invoices() {
  const { t } = useTranslation();
  const { config } = useAppContext();
  const finLabels = getDomainConfig().financialLabels;
  const [invoiceGlobalFilter, setInvoiceGlobalFilter] = useState("");
  const [invoiceData, setInvoiceData] = useState([]);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const isClientUser = localStorage.getItem("UserRole") === USER_ROLES.CLIENT;

    useEffect(() => {
        const fetchInvoices = async () => {
            setInvoiceLoading(true);
            const customerId = localStorage.getItem("customerId");
            const locationId = localStorage.getItem("LocationId");
            const userId = localStorage.getItem("UserId");

            try {
                let response;
                if (isClientUser) {
                    // Client — Show the related invoices only
                    const userRes = await axios.get(
                        `${process.env.REACT_APP_API_URL}/userRoute/getUserById/${userId}`,
                        { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
                    );
                    const relationId = userRes.data.RelationId;

                    response = await axios.get(
                        `${process.env.REACT_APP_API_URL}/clientInvoiceRoute/getInvoicesByRelation`,
                        {
                            params: { customerId, relationId },
                            headers: { Authorization: process.env.REACT_APP_BasicAuthSecret },
                        }
                    );
                } else {
                    // App Owner/Admin — Show the invoice for all customer 
                    response = await axios.get(
                        `${process.env.REACT_APP_API_URL}/clientInvoiceRoute/getAllInvoices`,
                        {
                            params: { customerId, locationId },
                            headers: { Authorization: process.env.REACT_APP_BasicAuthSecret },
                        }
                    );
                }
            setInvoiceData(response.data);
            } catch (error) {
            console.error("Error fetching invoices:", error);
            } finally {
            setInvoiceLoading(false);
            }
        };
        fetchInvoices();
    }, [config.location]);

    const filteredInvoiceData = useMemo(() => {
        if (!invoiceGlobalFilter) return invoiceData;
        return invoiceData.filter((invoice) =>
            invoice.InvoiceNumber?.toLowerCase().includes(invoiceGlobalFilter.toLowerCase()) ||
            invoice.Billing?.toLowerCase().includes(invoiceGlobalFilter.toLowerCase()) ||
            invoice.ClientName?.toLowerCase().includes(invoiceGlobalFilter.toLowerCase()) ||
            invoice.Amount?.toString().includes(invoiceGlobalFilter) ||
            invoice.TotalHours?.toString().includes(invoiceGlobalFilter)
        );
    }, [invoiceData, invoiceGlobalFilter]);

    const handleViewInvoice = async (invoiceNumber) => {
        try {
            const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/clientInvoiceRoute/invoiceFile/${invoiceNumber}?type=view`,
            {
                responseType: "blob",
                headers: {
                Authorization: process.env.REACT_APP_BasicAuthSecret,
                },
            }
            );
            const blob = new Blob([response.data], { type: "application/pdf" });
            const url = window.URL.createObjectURL(blob);
            window.open(url);
        } catch (error) {
            console.error("View failed:", error);
        }
    };

    const handleDownloadInvoice = async (invoiceNumber, clientName) => {
        try {
            const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/clientInvoiceRoute/invoiceFile/${invoiceNumber}?type=download`,
            {
                responseType: "blob",
                headers: {
                Authorization: process.env.REACT_APP_BasicAuthSecret,
                },
            }
            );
            const blob = new Blob([response.data], { type: "application/pdf" });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            const safeName = (clientName || "Client")
            .replace(/\s+/g, "_")
            .replace(/[^a-zA-Z0-9_]/g, "");
            link.download = `${invoiceNumber}_${safeName}.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error("Download failed:", error);
        }
    };

    const invoiceColumns = [
      ...(!isClientUser ? [{
        accessorKey: "ClientName",
        header: t("clientName"),
        meta: {
        headerProps: { className: "text-900" },
        cellProps: { className: "py-2" },
        },
        cell: ({ row: { original } }) => (
        <span>{original.ClientName || "--"}</span>
        ),
      }] : []),
    {
        accessorKey: "InvoiceNumber",
        header: t("invoiceNumber"),
        meta: {
        headerProps: { className: "text-900" },
        cellProps: { className: "py-2" },
        },
        cell: ({ row: { original } }) => (
        <span>{original.InvoiceNumber || "--"}</span>
        ),
    },
    {
        accessorKey: "InvoiceDate",
        header: t("invoiceDate"),
        meta: {
        headerProps: { className: "text-900" },
        },
        cell: ({ row: { original } }) => (
        <span>{formatDate(original.InvoiceDate) || "--"}</span>
        ),
    },
    {
        accessorKey: "Billing",
        header: t("billing"),
        meta: {
        headerProps: { className: "text-900" },
        },
        cell: ({ row: { original } }) => (
        <span>{original.Billing || "--"}</span>
        ),
    },
    {
        accessorKey: "TotalHours",
        header: t("totalHours"),
        meta: {
        headerProps: { className: "text-900" },
        },
        cell: ({ row: { original } }) => (
        <span>
            {original.TotalHours != null
            ? formatHoursVerbose(Number(original.TotalHours))
            : "--"}
        </span>
        ),
    },
    {
        accessorKey: "Amount",
        header: t("amount"),
        meta: {
        headerProps: { className: "text-900" },
        },
        cell: ({ row: { original } }) => (
        <span>
            {original.Amount != null
            ? new Intl.NumberFormat(finLabels.currencyLocale, {
                style: "currency",
                currency: finLabels.currencyCode,
                }).format(Number(original.Amount))
            : "--"}
        </span>
        ),
    },
    {
        accessorKey: "Action",
        header: t("action"),
        meta: {
        headerProps: { className: "text-900 text-center" },
        cellProps: { className: "text-center" },
        },
        cell: ({ row: { original } }) => (
        <div className="d-flex justify-content-center gap-3">
            <FontAwesomeIcon
            title={t("view")}
            icon={faEye}
            className="text-info cursor-pointer"
            onClick={() => handleViewInvoice(original.InvoiceNumber)}
            />
            <FontAwesomeIcon
            title={t("download")}
            icon={faDownload}
            className="text-primary cursor-pointer"
            onClick={() =>
                handleDownloadInvoice(original.InvoiceNumber, original.ClientName)
            }
            />
        </div>
        ),
    },
    ];

    const invoiceTable = useAdvanceTable({
        data: filteredInvoiceData,
        columns: invoiceColumns,
        selection: false,
        sortable: true,
        pagination: true,
        perPage: getInitialPageSize("invoiceTableInRelationDetails"),
    });

  return (
    <>
      <Row className="g-3 mb-3">
        <Col lg={12} xxl={12}>
          <Card className="mb-3 h-100">
            <Background image={corner4} className="bg-card" />
            <Card.Body className="position-relative pe-3 pb-2">
              <Row className="flex-between-center">
                <Col md>
                  <h5 className="mb-2 mb-md-0">{t("invoices")}</h5>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

    <AdvanceTableProvider {...invoiceTable}>
      <Card className="mb-3">
        <Card.Header>
          <Row className="align-items-center justify-content-end">
            <Col sm="auto">
              <AdvanceTableSearchBox setGlobalFilter={setInvoiceGlobalFilter} />
            </Col>
          </Row>
        </Card.Header>

        <Card.Body className="p-0">
          {invoiceLoading ? (
            <div className="text-center p-4">
              <Spinner animation="border" />
            </div>
          ) : (
            <>
              <AdvanceTable
                columns={invoiceColumns}
                data={filteredInvoiceData}
                headerClassName="bg-200 text-nowrap align-middle"
                rowClassName="align-middle white-space-nowrap"
                tableProps={{
                  bordered: true,
                  striped: true,
                  size: "sm",
                  className: "mb-0 overflow-hidden data-font-size table-hover",
                }}
              />
                {filteredInvoiceData.length === 0 && (
                <div className="text-center pt-4">{t("noDataAvailableText")}</div>
                )}
            </>
          )}
        </Card.Body>
        <Card.Footer>
          <AdvanceTableFooter
            rowInfo
            navButtons
            rowsPerPageSelection
            tableKey="invoiceTableInRelationDetails"
          />
        </Card.Footer>
      </Card>
    </AdvanceTableProvider>
    </>
  );
}

export default Invoices;